import { ModuleDocContent } from "./docsData";

export const templateModuleDocs: Record<string, ModuleDocContent> = {
  "email-templates": {
    title: "Email Templates",
    subtitle: "Manage Email Notification Templates",
    color: "slate",
    badge: "Enterprise",
    description:
      "View and customize email templates used across the platform. Design emails visually with the GrapesJS MJML editor — drag-and-drop blocks, insert dynamic variables, and preview responsive output with sample data.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Email Templates</strong> lets you manage the email notifications sent by the platform. Browse all templates, filter by type, and design content using the new <strong>GrapesJS MJML visual editor</strong> with dynamic variable support and responsive preview.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/6f61c2fb-cbd7-40d1-92ba-1c574bfb2eb5.png" alt="Email Template List" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Email template list</p></div>

      <section>
        <h2>Template List</h2>
        <p>All email templates are displayed in a paginated grid with the following details:</p>
        <table>
          <thead>
            <tr>
              <th>Column</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Template</strong></td>
              <td>Template name with subject line displayed below.</td>
            </tr>
            <tr>
              <td><strong>Type</strong></td>
              <td>Template type badge (e.g., WELCOME_EMAIL, COURSE_ASSIGNED).</td>
            </tr>
            <tr>
              <td><strong>Updated</strong></td>
              <td>Last modified date.</td>
            </tr>
            <tr>
              <td><strong>Actions</strong></td>
              <td>Edit button to open the template editor.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Filters</h2>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Search</strong></td>
              <td>Search templates by name or key (debounced).</td>
            </tr>
            <tr>
              <td><strong>Type</strong></td>
              <td>Filter by template type from a dynamically loaded dropdown.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/c7caed6e-2ee8-465d-8fa1-c5358fade0f4.png" alt="GrapesJS Email Template Editor" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: GrapesJS MJML email template editor</p></div>

      <section>
        <h2>Template Editor (GrapesJS MJML)</h2>
        <p>The editor is a full-screen workspace powered by <strong>GrapesJS + MJML</strong>. MJML compiles to bulletproof, responsive HTML that renders consistently across email clients (Gmail, Outlook, Apple Mail, mobile clients). The layout consists of a sticky header, a left <strong>Variables panel</strong>, a central <strong>canvas</strong>, and the GrapesJS <strong>blocks / layers / styles</strong> panel on the right.</p>

        <h3>Basic Information</h3>
        <table>
          <thead>
            <tr>
              <th>Field</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Template Key</strong></td>
              <td>Unique identifier (alphanumeric and underscores). Disabled in edit mode.</td>
            </tr>
            <tr>
              <td><strong>Template Name</strong></td>
              <td>Display name for the template (required).</td>
            </tr>
            <tr>
              <td><strong>Template Type</strong></td>
              <td>Searchable dropdown to select the template type. Drives the variable list loaded in the left panel.</td>
            </tr>
            <tr>
              <td><strong>Subject</strong></td>
              <td>Email subject input in the header. Supports <code>{{variable_name}}</code> placeholders — click a variable in the left panel to insert it at the cursor.</td>
            </tr>
          </tbody>
        </table>

        <h3>Visual Canvas &amp; Blocks</h3>
        <p>The canvas renders the MJML email live. Drag <strong>blocks</strong> from the right panel onto sections and columns to compose the layout. Available MJML blocks include:</p>
        <table>
          <thead>
            <tr>
              <th>Block</th>
              <th>Use</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Section / Column / Wrapper / Group</strong></td><td>Structural containers — the foundation of every MJML email layout.</td></tr>
            <tr><td><strong>Text</strong></td><td>Rich-text editable paragraph. Double-click to open the inline RTE (bold, italic, link, lists, alignment).</td></tr>
            <tr><td><strong>Button</strong></td><td>Call-to-action button with background color, border radius, and <code>href</code>.</td></tr>
            <tr><td><strong>Image</strong></td><td>Responsive image with alt text and link support.</td></tr>
            <tr><td><strong>Divider / Spacer</strong></td><td>Horizontal rule and vertical spacing.</td></tr>
            <tr><td><strong>Hero / Navbar / Social</strong></td><td>Prebuilt hero banners, navigation bars, and social-media icon rows.</td></tr>
          </tbody>
        </table>

        <h3>Styling &amp; Layers</h3>
        <p>Select any block on the canvas to style it in the right sidebar:</p>
        <table>
          <thead>
            <tr>
              <th>Panel</th>
              <th>What you can do</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Styles</strong></td><td>Edit colors, typography, padding, borders, background, and alignment per block.</td></tr>
            <tr><td><strong>Settings</strong></td><td>Tweak MJML-specific attributes like <code>href</code>, <code>align</code>, <code>font-family</code>, <code>width</code>.</td></tr>
            <tr><td><strong>Layers</strong></td><td>Tree view of the MJML structure — useful for selecting nested elements and reordering.</td></tr>
          </tbody>
        </table>

        <h3>Device Preview</h3>
        <p>Switch between <strong>Desktop</strong>, <strong>Tablet</strong>, and <strong>Mobile</strong> views in the top toolbar to verify responsive behavior. MJML automatically stacks columns on mobile.</p>

        <h3>Variables Panel</h3>
        <p>A sticky sidebar on the left displays available variables for the selected template type. <strong>Drag</strong> a variable onto the canvas to drop a <code>{{variable_name}}</code> token into the target text block, or <strong>click</strong> to insert it into the subject field at cursor position. Variables are auto-detected from the compiled HTML on save.</p>

        <h3>Preview</h3>
        <p>Click <strong>Preview</strong> in the header to open an overlay showing the rendered email with auto-generated sample data substituted for all detected variables. Unresolved placeholders are highlighted in yellow so you can spot missing variables instantly.</p>

        <h3>Save Behavior</h3>
        <p>On save, the editor:</p>
        <ul>
          <li>Runs the <strong>mjml-compile-safe</strong> command to generate responsive HTML from the MJML canvas.</li>
          <li>Stores both the compiled <code>html_template</code> and the editor's <code>editor_json</code> so the canvas reloads exactly as designed.</li>
          <li>Extracts all <code>{{variable_name}}</code> tokens and updates <code>allowed_variables</code> automatically.</li>
          <li>Guards against accidental blank saves — if the compile produces an empty body, the last-known-good HTML is preserved.</li>
        </ul>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Customize welcome email</div>
            <div class="sz-use-case-flow">
              Find the welcome email template using the Type filter. Click Edit, drag Section / Text / Button blocks onto the canvas to shape the layout. Apply your brand colors via the Styles panel. Drag variables from the left panel for the learner's name and login link. Use device preview to verify mobile rendering, then Preview and Save.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Update notification wording</div>
            <div class="sz-use-case-flow">
              Search for the relevant template by name, open the editor, double-click any text block to edit inline with the rich-text toolbar. Adjust the subject in the header. Preview with sample data and save.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Rebuild a template from scratch</div>
            <div class="sz-use-case-flow">
              Select all blocks on the canvas and delete. Drag a fresh Section, add Columns, then drop Text / Image / Button blocks inside. Configure each in the Styles / Settings panel. Insert variables via drag-and-drop for personalization.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Always use the <strong>device preview</strong> toolbar and the full <strong>Preview</strong> overlay before saving — MJML's responsive stacking can look different from the desktop canvas. Drag variables from the sidebar instead of typing them manually to avoid typos.</p>
      </div>

    </div>`,
  },
  "certificate-templates": {
    title: "Certificate Templates",
    subtitle: "Visual Certificate Designer",
    color: "slate",
    badge: "Enterprise",
    description:
      "Design and manage certificate templates with a Canva-like visual editor. Drag and drop text, variables, shapes, and images onto a canvas. Configure backgrounds, borders, and element styles. Preview and export as PNG or PDF.",
    content: `<div class="space-y-10">

      <div class="sz-callout sz-callout-info">
        <p><strong>Certificate Templates</strong> provides a visual drag-and-drop editor for designing certificate layouts. Create templates for course completions, certifications, and learning paths with dynamic variables, custom styling, and live preview.</p>
      </div>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/a0b7a072-ba8a-4f12-8756-a6c2bd1b5c6f.png" alt="Certificate Template List" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Certificate template list</p></div>

      <section>
        <h2>Template List</h2>
        <p>Templates are displayed as a card grid with live certificate previews rendered via iframe.</p>
        <table>
          <thead>
            <tr>
              <th>Element</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Preview</strong></td>
              <td>Live rendered preview of the certificate HTML template.</td>
            </tr>
            <tr>
              <td><strong>Template Name</strong></td>
              <td>Name with template key badge.</td>
            </tr>
            <tr>
              <td><strong>Type</strong></td>
              <td>Color-coded badge: Course Completion, Learning Path Completion, or Certification tiers (Gold, Silver, Bronze).</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>Active or Inactive status badge.</td>
            </tr>
            <tr>
              <td><strong>Variables</strong></td>
              <td>Count of allowed dynamic variables.</td>
            </tr>
            <tr>
              <td><strong>Default</strong></td>
              <td>Star badge indicating the default template for its type.</td>
            </tr>
          </tbody>
        </table>

        <h3>Filters</h3>
        <table>
          <thead>
            <tr>
              <th>Filter</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Search</strong></td>
              <td>Search by template name or key.</td>
            </tr>
            <tr>
              <td><strong>Type</strong></td>
              <td>All Types, Course Completion, Learning Path Completion, Certification tiers.</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>All Status, Active, Inactive.</td>
            </tr>
          </tbody>
        </table>

        <h3>Actions</h3>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Create Template</strong></td>
              <td>Open the visual editor with a blank canvas or choose from featured preset templates.</td>
            </tr>
            <tr>
              <td><strong>Edit</strong></td>
              <td>Open the template in the visual editor.</td>
            </tr>
            <tr>
              <td><strong>Set as Default</strong></td>
              <td>Mark the template as the default for its certificate type.</td>
            </tr>
            <tr>
              <td><strong>Delete</strong></td>
              <td>Remove the template with confirmation.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <div class="sz-screenshot"><img src="https://sgp1.digitaloceanspaces.com/skillzen-local/b9a3d673-8bb6-4959-82a5-31152be8063e.png" alt="Certificate Template Editor" loading="lazy" /><p style="text-align:center;color:#9ca3af;font-size:0.75rem;padding:0.5rem;">Screenshot: Certificate template visual editor</p></div>

      <section>
        <h2>Visual Editor</h2>
        <p>A Canva-like builder with three panels: Elements (left), Canvas (center), and Properties (right).</p>

        <h3>Elements Panel</h3>
        <p>Drag elements from the left panel onto the canvas or click to add:</p>
        <table>
          <thead>
            <tr>
              <th>Category</th>
              <th>Elements</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Quick Add</strong></td>
              <td>Certificate Title, Recipient Name, Subtitle, Course/Program Name, Issue Date — preset elements with sensible defaults.</td>
            </tr>
            <tr>
              <td><strong>Text Elements</strong></td>
              <td>Heading, Subheading, Body Text, Label — each with preset font sizes and weights.</td>
            </tr>
            <tr>
              <td><strong>Dynamic Variables</strong></td>
              <td>All available variables for the selected template type (e.g., holder_name, issued_date, course_title). Inserted as <code>{{variable_name}}</code> placeholders.</td>
            </tr>
            <tr>
              <td><strong>Shapes &amp; Lines</strong></td>
              <td>Rectangle, Circle, Horizontal Line, Gold Line (decorative).</td>
            </tr>
            <tr>
              <td><strong>Media</strong></td>
              <td>Logo Image with URL input.</td>
            </tr>
          </tbody>
        </table>

        <h3>Canvas</h3>
        <table>
          <thead>
            <tr>
              <th>Feature</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Size</strong></td>
              <td>842x595px (landscape) or 595x842px (portrait).</td>
            </tr>
            <tr>
              <td><strong>Grid</strong></td>
              <td>20px snap grid (toggleable).</td>
            </tr>
            <tr>
              <td><strong>Zoom</strong></td>
              <td>25% to 200% with fit-to-screen button.</td>
            </tr>
            <tr>
              <td><strong>Element Controls</strong></td>
              <td>8 resize handles, drag to move, lock/visibility toggles, duplicate, z-index ordering.</td>
            </tr>
            <tr>
              <td><strong>Background</strong></td>
              <td>Solid color, gradient (linear/radial), or image (cover/contain/fill/tile).</td>
            </tr>
          </tbody>
        </table>

        <h3>Properties Panel</h3>
        <p>When an element is selected, configure its properties:</p>
        <table>
          <thead>
            <tr>
              <th>Property Group</th>
              <th>Options</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Position &amp; Size</strong></td>
              <td>X, Y, Width, Height, Rotation.</td>
            </tr>
            <tr>
              <td><strong>Text Styling</strong></td>
              <td>Font family, size, weight, style, decoration, alignment, line height, letter spacing, color.</td>
            </tr>
            <tr>
              <td><strong>Shape Styling</strong></td>
              <td>Fill color, border (width, style, color, radius), shadow.</td>
            </tr>
            <tr>
              <td><strong>Appearance</strong></td>
              <td>Opacity, visibility, and lock toggles.</td>
            </tr>
          </tbody>
        </table>

        <h3>Keyboard Shortcuts</h3>
        <table>
          <thead>
            <tr>
              <th>Shortcut</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Delete / Backspace</strong></td><td>Remove selected element.</td></tr>
            <tr><td><strong>Ctrl+Z / Ctrl+Y</strong></td><td>Undo / Redo.</td></tr>
            <tr><td><strong>Ctrl+D</strong></td><td>Duplicate selected element.</td></tr>
            <tr><td><strong>Arrow keys</strong></td><td>Move element (hold Shift for 10px steps).</td></tr>
            <tr><td><strong>Ctrl++ / Ctrl+-</strong></td><td>Zoom in / out.</td></tr>
            <tr><td><strong>Escape</strong></td><td>Deselect element.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Toolbar Actions</h2>
        <table>
          <thead>
            <tr>
              <th>Action</th>
              <th>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr><td><strong>Save / Update</strong></td><td>Persist the template to the server.</td></tr>
            <tr><td><strong>Preview</strong></td><td>Toggle live preview with sample data.</td></tr>
            <tr><td><strong>Undo / Redo</strong></td><td>Navigate edit history.</td></tr>
            <tr><td><strong>Duplicate / Delete</strong></td><td>Clone or remove the selected element.</td></tr>
            <tr><td><strong>Bring Forward / Send Backward</strong></td><td>Adjust element z-index layering.</td></tr>
            <tr><td><strong>Align Center</strong></td><td>Center element horizontally or vertically on the canvas.</td></tr>
            <tr><td><strong>Export PNG / PDF</strong></td><td>Download the certificate as an image or PDF file.</td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h2>Common use cases</h2>
        <div class="sz-use-cases">
          <div class="sz-use-case">
            <div class="sz-use-case-title">Create a branded certificate</div>
            <div class="sz-use-case-flow">
              Click Create Template, choose a preset or start blank. Add your company logo, set the background color or gradient to match your brand. Use Quick Add to place the title, recipient name, and issue date. Adjust fonts and colors in the Properties panel. Save and set as default.
            </div>
          </div>
          <div class="sz-use-case">
            <div class="sz-use-case-title">Customize for different certificate types</div>
            <div class="sz-use-case-flow">
              Create separate templates for Course Completion, Certification (Gold/Silver/Bronze tiers), and Learning Path Completion. Use type-specific variables and distinct color schemes. Set each as the default for its type.
            </div>
          </div>
        </div>
      </section>

      <div class="sz-callout sz-callout-tip">
        <div class="sz-callout-label">Tip</div>
        <p>Use the <strong>Quick Add</strong> elements to rapidly scaffold a certificate layout, then fine-tune positioning and styling in the Properties panel. Export a PNG preview before setting the template as default.</p>
      </div>

    </div>`,
  }
};